import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { FilterAltIcon } from 'cokitchen-icons';
import { useSearchParams } from 'react-router-dom';
import PageHeaderText from 'components/base/page-header-text';
import { debounce, formatNumberWithCommas } from 'utils/functions';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import {
  normalizeStoreType,
  StoreTypeParam,
  STORE_TYPE_LABELS
} from 'pages/shared/inventory-stock/types';
import useDashboardContext from 'contexts/dashboard.context';
import { RequestItemType } from 'models/item-request.model';
import * as XLSX from 'xlsx';
import { StoreItemType } from 'models/store-item.model';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const tableHeaders = ['Item name', 'Min. stock level', ''];

type MinFilterType = 'ALL' | 'SET' | 'NOT_SET';

const MIN_FILTER_OPTIONS: { label: string; value: MinFilterType }[] = [
  { label: 'All items', value: 'ALL' },
  { label: 'Min. stock level set', value: 'SET' },
  { label: 'Min. stock level not set', value: 'NOT_SET' }
];

type MinimumStockSettingsProps = {
  defaultStoreType: StoreTypeParam;
  allowedStoreTypes: StoreTypeParam[];
  showStoreTypeDropdown?: boolean;
  dropdownStoreTypes?: StoreTypeParam[];
};

const buildStoreTypeOptions = (types: StoreTypeParam[]) =>
  types.map((value) => ({ label: STORE_TYPE_LABELS[value], value }));

const MinimumStockSettings = ({
  defaultStoreType,
  allowedStoreTypes,
  showStoreTypeDropdown = true,
  dropdownStoreTypes
}: MinimumStockSettingsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeTypeParam = showStoreTypeDropdown
    ? searchParams.get('storeType') ?? undefined
    : undefined;
  const normalizedStoreType = normalizeStoreType(storeTypeParam, defaultStoreType);
  const storeType = allowedStoreTypes.includes(normalizedStoreType)
    ? normalizedStoreType
    : defaultStoreType;
  const storeTypeOptions = useMemo(
    () => buildStoreTypeOptions(dropdownStoreTypes ?? allowedStoreTypes),
    [allowedStoreTypes, dropdownStoreTypes]
  );
  const minQuantityPrefix = storeType === 'main-store' ? 'main-store' : 'kitchen-store';
  const minQuantityItemType =
    storeType === 'prep-store' ? 'prep' : storeType === 'brand-chef-store' ? 'meal' : undefined;

  const [storeArray, setStoreArray] = useState<RequestItemType[]>([]);
  const [isFetchingStoreItems, setIsFetchingStoreItems] = useState(false);
  const [totalStoreItems, setTotalStoreItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [minFilter, setMinFilter] = useState<MinFilterType>('ALL');
  const [editingAll, setEditingAll] = useState(false);
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});
  const [editedMinQuantities, setEditedMinQuantities] = useState<Record<string, string>>({});
  const [ingredientsPool, setIngredientsPool] = useState([] as StoreItemType[]);
  const [isUpdatingAll, setIsUpdatingAll] = useState(false);
  const [updatingRows, setUpdatingRows] = useState<Record<string, boolean>>({});
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const fetchStoreItems = useCallback(
    async (searchString: string, current_page?: number) => {
      if (!activeStore) return;
      try {
        setEditedMinQuantities({});
        setIsFetchingStoreItems(true);
        const endpoint =
          storeType === 'main-store'
            ? `main-store/${activeStore.id}/items`
            : `kitchen-store/${activeStore.id}/store-items`;
        const response = await apiService.get(`${endpoint}`, {
          params: {
            name: encodeURIComponent(searchString),
            page: current_page || '1',
            item_type: minQuantityItemType
          },
          version: 'v2'
        });
        if (response) {
          setStoreArray(response?.data?.data || []);
          setLastPage(
            Math.max(
              1,
              Math.ceil(response?.data?.pagination.total / response?.data?.pagination.page_size)
            )
          );
          setTotalStoreItems(response?.data?.pagination.total || 0);
        }
      } catch (err: any) {
        toast(
          <ToastMessage
            message={err.response?.data?.message || 'Error fetching store items'}
            type='error'
          />
        );
      } finally {
        setIsFetchingStoreItems(false);
      }
    },
    [storeType]
  );

  const debounceFetchStoreItems = useMemo(() => debounce(fetchStoreItems, 500), [fetchStoreItems]);

  useEffect(() => {
    setIsFetchingStoreItems(true);
    setStoreArray([]);
    debounceFetchStoreItems(searchValue, currentPage);
  }, [searchValue, currentPage, debounceFetchStoreItems]);

  useEffect(() => {
    setEditedMinQuantities((prev) => {
      const next = { ...prev };
      storeArray.forEach((item) => {
        if (next[item.id] === undefined) {
          const minQuantity = Number(item?.min_quantity ?? 0);
          next[item.id] = minQuantity ? String(minQuantity) : '';
        }
      });
      return next;
    });
  }, [storeArray]);

  useEffect(() => {
    setCurrentPage(1);
  }, [storeType]);

  const filteredItems = useMemo(() => {
    return storeArray.filter((item) => {
      const savedMinQuantity = Number(item?.min_quantity ?? 0);
      if (minFilter === 'SET') {
        return savedMinQuantity > 0;
      }
      if (minFilter === 'NOT_SET') {
        return savedMinQuantity === 0;
      }
      return true;
    });
  }, [storeArray, minFilter]);

  const handleRowEditToggle = (id: string) => {
    setEditingRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFileUpload = (e: any) => {
    if (e.target.files) {
      readFile(e.target.files?.[0]);
    }
  };

  const readFile = (file: any) => {
    if (file) {
      setIsUpdatingAll(true);
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const mealDetails = workbook.SheetNames[0];
        const convertedData = XLSX.utils.sheet_to_json(workbook.Sheets[mealDetails]);
        const formattedData: any = {};
        convertedData.map((item: any) => {
          const ingredientId =
            ingredientsPool.find(
              (storeItem) =>
                item['Name'].toLowerCase().trim() === storeItem.name.toLowerCase().trim()
            )?.id ?? '';
          formattedData[ingredientId] = item['Min Quantity'];
        });
        onUpdateMinimumQuantity(formattedData);
        fetchStoreItems(searchValue, currentPage);
      };
    }
  };

  const handleMinQuantityChange = async (id: string, value: string) => {
    if (!canWriteInventoryManagement) return;
    if (!/^\d*$/.test(value)) return;
    setEditedMinQuantities((prev) => ({ ...prev, [id]: value }));
  };

  const handleSingleQuantityChange = async (id: string, value: string) => {
    if (!canWriteInventoryManagement) return;
    try {
      setUpdatingRows((prev) => ({ ...prev, [id]: true }));
      const minQuantity = Number(value || 0);
      const payload = {
        items: [
          {
            store_item_id: id,
            min_quantity: minQuantity
          }
        ]
      };
      await apiService.put(`${minQuantityPrefix}/min-quantity`, {
        version: 'v2',
        data: payload,
        params: minQuantityItemType ? { item_type: minQuantityItemType } : undefined
      });
      setEditingRows((prev) => ({ ...prev, [id]: false }));
      toast(<ToastMessage message={'Item quantity updated'} type='success' />);
    } catch (e: any) {
      toast(<ToastMessage message={'Error updating items'} type='error' />);
    } finally {
      setUpdatingRows((prev) => ({ ...prev, [id]: false }));
    }
  };

  const minFilterLabel =
    MIN_FILTER_OPTIONS.find((option) => option.value === minFilter)?.label || 'All items';
  const storeTypeLabel =
    storeTypeOptions.find((option) => option.value === storeType)?.label ||
    STORE_TYPE_LABELS[defaultStoreType];

  const onUpdateMinimumQuantity = async (csvData?: Record<string, string>) => {
    if (!canWriteInventoryManagement) return;
    const data = csvData ?? editedMinQuantities;
    try {
      setIsUpdatingAll(true);
      const payload = {
        items: Object.entries(data).map((item) => {
          return {
            store_item_id: item[0],
            min_quantity: Number(item[1])
          };
        })
      };
      await apiService.put(`${minQuantityPrefix}/min-quantity`, {
        data: payload,
        version: 'v2',
        params: minQuantityItemType ? { item_type: minQuantityItemType } : undefined
      });
      setEditingAll(false);
      toast(<ToastMessage message={'Item quantity updated'} type='success' />);
    } catch (e: any) {
      toast(<ToastMessage message={'Error updating items'} type='error' />);
    } finally {
      setIsUpdatingAll(false);
    }
  };
  const handleConfirm = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const getAllIngredients = async () => {
    try {
      const prefix = storeType === 'main-store' ? 'main-store' : 'kitchen-store';
      const res = await apiService.get(`${prefix}/lean`, {
        params: {
          store_id: activeStore?.id
        },
        version: 'v2'
      });
      setIngredientsPool(res.data.data);
    } catch (e: any) {
      console.log(e);
    }
  };
  useEffect(() => {
    getAllIngredients();
  }, []);
  return (
    <div className='px-2 md:px-6 mt-2'>
      <PageHeaderText
        title={`Minimum Stock Settings${
          totalStoreItems ? ` (${totalStoreItems})` : isFetchingStoreItems ? ' (**)' : ''
        }`}
        description='Manage minimum stock levels for all items.'>
        {canWriteInventoryManagement ? (
          <>
            <Button
              loading={isUpdatingAll}
              onClick={handleConfirm}
              variant='block'
              color='primary'
              small>
              {isUpdatingAll ? 'Updating ' : ' Upload Csv'}
            </Button>
            <input
              ref={fileUploadRef}
              type='file'
              accept='.xlsx, .xls, .csv'
              hidden
              onChange={handleFileUpload}
            />
          </>
        ) : null}
      </PageHeaderText>

      <div className='flex flex-wrap items-center justify-between gap-4'>
        <div className='flex flex-wrap items-center gap-3'>
          <Input
            type='search'
            placeholder='Search by item name'
            className='w-[280px]'
            inputClass='h-[36px]'
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />

          {showStoreTypeDropdown && (
            <div className='h-[36px]'>
              <Dropdown
                hideArrow
                placement='bottom-start'
                content={
                  <div className='border border-[#E5E6E6] rounded-[6px] text-xs leading-[19px] h-[36px] flex px-3 justify-between items-center w-[190px] cursor-pointer'>
                    <span className='text-[#5E6164] text-sm truncate'>{storeTypeLabel}</span>
                    <FilterAltIcon className='text-[#5E6164]' />
                  </div>
                }>
                <DropdownList>
                  {storeTypeOptions.map((option) => (
                    <DropdownItem
                      key={option.value}
                      className={storeType === option.value ? 'text-red-5' : ''}
                      onClick={() => {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('storeType', option.value);
                          return next;
                        });
                      }}>
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </Dropdown>
            </div>
          )}

          <div className='h-[36px]'>
            <Dropdown
              hideArrow
              placement='bottom-start'
              content={
                <div className='border border-[#E5E6E6] rounded-[6px] text-xs leading-[19px] h-[36px] flex px-3 justify-between items-center w-[190px] cursor-pointer'>
                  <span className='text-[#5E6164] text-sm truncate'>{minFilterLabel}</span>
                  <FilterAltIcon className='text-[#5E6164]' />
                </div>
              }>
              <DropdownList>
                {MIN_FILTER_OPTIONS.map((option) => (
                  <DropdownItem
                    key={option.value}
                    className={minFilter === option.value ? 'text-red-5' : ''}
                    onClick={() => setMinFilter(option.value)}>
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>
          {canWriteInventoryManagement && editingAll ? (
            <button
              type='button'
              onClick={() => onUpdateMinimumQuantity()}
              className='flex items-center gap-2  rounded-[18px] border border-red-5 text-red-5 px-4 h-[36px] text-xs'
              disabled={isUpdatingAll}>
              {isUpdatingAll ? (
                <span className='h-[14px] w-[14px] animate-spin rounded-full border-2 border-red-5 border-t-transparent' />
              ) : (
                'Done'
              )}
            </button>
          ) : canWriteInventoryManagement ? (
            <button
              className='flex items-center gap-2  rounded-[18px] border border-red-5 text-red-5 px-4 h-[36px] text-xs'
              onClick={() => setEditingAll(true)}
              disabled={isUpdatingAll}>
              <EditIcon className='w-[14px] h-[14px] ' />
              Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className='mt-6 w-[631px]'>
        {filteredItems.length === 0 && !isFetchingStoreItems ? (
          <div className='flex justify-center mt-18'>
            <p className='text-gray-500'>No Items Found in {storeTypeLabel}</p>
          </div>
        ) : (
          <Table headers={tableHeaders} loading={isFetchingStoreItems}>
            {filteredItems.map((item) => {
              const isEditable = editingAll || !!editingRows[item.id];
              const originalMinQuantity = Number(item?.min_quantity ?? 0);
              const editableValue =
                editedMinQuantities[item.id] ??
                (originalMinQuantity ? String(originalMinQuantity) : '');
              const displayMinQuantity = Number(editableValue || originalMinQuantity || 0);
              const minQuantityLabel =
                displayMinQuantity > 0 ? formatNumberWithCommas(displayMinQuantity) : 'Not set';
              const minQuantityClass = displayMinQuantity > 0 ? 'text-gray-10' : 'text-red-5';
              const isUpdated =
                editedMinQuantities[item.id] !== undefined &&
                editableValue !== (originalMinQuantity ? String(originalMinQuantity) : '');
              const isRowUpdating = !!updatingRows[item.id];

              return (
                <TableRow key={item.id} className='text-gray-12'>
                  <TableData className='text-gray-10'>{item.ingredient?.name}</TableData>
                  <TableData>
                    {isEditable && canWriteInventoryManagement ? (
                      <input
                        type='number'
                        min='0'
                        className='h-[28px] w-[90px] rounded-[4px] border border-[#E5E6E6] px-2 text-sm text-gray-10'
                        value={editableValue}
                        onChange={(e) => handleMinQuantityChange(item.id, e.target.value)}
                      />
                    ) : (
                      <span className={minQuantityClass}>{minQuantityLabel}</span>
                    )}
                  </TableData>
                  <TableData>
                    <button
                      type='button'
                      className='inline-flex items-center justify-center'
                      disabled={isRowUpdating}>
                      {isRowUpdating ? (
                        <span className='h-[14px] w-[14px] animate-spin rounded-full border-2 border-gray-6 border-t-transparent' />
                      ) : isEditable && !editingAll && canWriteInventoryManagement ? (
                        <span
                          onClick={() => {
                            if (!editingAll) {
                              handleSingleQuantityChange(item.id, editableValue);
                            }
                          }}
                          className={isUpdated ? 'text-xs text-red-5' : 'text-xs text-gray-6'}>
                          Done
                        </span>
                      ) : canWriteInventoryManagement ? (
                        <EditIcon
                          onClick={() => handleRowEditToggle(item.id)}
                          className='w-[16px] h-[16px] text-gray-6'
                        />
                      ) : (
                        <span className='text-xs text-gray-6'>-</span>
                      )}
                    </button>
                  </TableData>
                </TableRow>
              );
            })}
          </Table>
        )}
      </div>
      {lastPage > 1 && (
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => setCurrentPage(e.selected + 1)}
        />
      )}
    </div>
  );
};

export default MinimumStockSettings;
